import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  CheckCircle2,
  ExternalLink,
  Plus,
  RotateCcw,
  Save,
  Send,
  Trash2,
  X,
} from 'lucide-react'
import {
  Button,
  Chip,
  ColumnConfigMenu,
  EmptyState,
  PartnerPicker,
  ProductPicker,
  SortableHeader,
  toast,
  useColumnConfig,
  type ColumnDef,
} from '@/components/ui'
import { SendQuoteDialog } from './SendQuoteDialog'
import type { PickerValue } from '@/components/ui/ProductPicker/ProductPicker'
import { ApiError } from '@/lib/api/jsonFetch'
import {
  useApproveQuote,
  useConvertToSo,
  useCreateQuote,
  useCustomerReject,
  useLineContext,
  usePoReceived,
  useQuote,
  useRejectQuote,
  useRenderQuotePdf,
  useSubmitQuote,
  useTransitionQuote,
  useUpdateQuote,
} from '../api/queries'
import type {
  Quote,
  QuoteLineRead,
  QuoteLineWrite,
  QuoteSummary,
  QuoteWrite,
} from '../api/types'
import { QuoteStatusChip } from './QuoteStatusChip'

/**
 * QuoteDetail (Sprint 12 Chunk 9, expanded post-ship to add the
 * create-and-edit form).
 *
 * Three modes:
 *   1. Create (`quoteId === null`) — new-quote form: customer
 *      (PartnerPicker), valid-through, payment terms, notes, line
 *      editor (product + qty + unit price). Save → calls onCreated
 *      with the new id so the parent selects it in the list.
 *   2. Edit-draft (`quoteId !== null && status in {draft, in_review}`)
 *      — same form, persists via PUT (replace-lines pattern).
 *      Approver-modify (D-6) uses the same form once status is
 *      in_review.
 *   3. Read-only + actions (every other status) — header card +
 *      lines table + status-driven action bar (Submit / Claim /
 *      Approve / Reject / Send / Record-PO / Convert-to-SO /
 *      Preview-PDF).
 */

const EDITABLE_STATUSES = new Set(['draft', 'in_review'])

/**
 * Line-editor column definitions. Order here is the default; user
 * preferences (visibility, order, sort) override via useColumnConfig
 * keyed by `quote-line-editor` in localStorage.
 *
 * `product` is sticky (can't be hidden or reordered) — every line
 * needs a product. `actions` is also sticky as the trash-button cell.
 */
const LINE_COLUMNS: ReadonlyArray<ColumnDef> = [
  { key: 'product', label: 'Product', sticky: true, width: 240, sortable: false },
  { key: 'customer_part_number', label: 'Cust. Part #', width: 140, sortable: true, align: 'left' },
  { key: 'quantity', label: 'Qty', width: 80, sortable: true, align: 'right' },
  { key: 'unit', label: 'UoM', width: 60, sortable: true, align: 'left' },
  { key: 'packaging', label: 'Packaging', width: 160, sortable: false, align: 'left' },
  { key: 'packaging_qty', label: 'Pkg Qty', width: 80, sortable: true, align: 'right' },
  { key: 'unit_price', label: 'Unit price', width: 110, sortable: true, align: 'right' },
  { key: 'last_paid', label: 'Last paid', width: 100, sortable: false, align: 'right' },
  { key: 'vendor_unit_cost', label: 'Cost', width: 100, sortable: true, align: 'right' },
  { key: 'margin', label: 'Margin %', width: 80, sortable: true, align: 'right' },
  { key: 'extended', label: 'Extended', width: 110, sortable: true, align: 'right' },
  { key: 'actions', label: '', sticky: true, width: 36, sortable: false },
]

/** Numeric/string accessor for sortRows. */
function lineSortValue(line: DraftLine, key: string): unknown {
  switch (key) {
    case 'customer_part_number': return line.customer_part_number || null
    case 'quantity': return line.quantity ? Number(line.quantity) : null
    case 'unit': return line.unit || null
    case 'packaging_qty': return line.packaging_qty ? Number(line.packaging_qty) : null
    case 'unit_price': return line.unit_price ? Number(line.unit_price) : null
    case 'vendor_unit_cost': return line.vendor_unit_cost ? Number(line.vendor_unit_cost) : null
    case 'margin': return lineMarginPct(line)
    case 'extended': return lineExtended(line)
    default: return null
  }
}

interface DraftLine {
  /** Local-only key for React list reconciliation. Negative for
   *  newly-added rows; positive matches the server `id` after save. */
  key: number
  product: PickerValue
  customer_part_number: string
  description: string
  quantity: string
  unit: string
  unit_price: string
  vendor_unit_cost: string
  lead_time_days: string
  selected_vendor_id: number | null
  /** Odoo product.packaging.id; nullable. Set by the Packaging dropdown. */
  packaging_id: number | null
  /** Number of packages (separate from `quantity`, which is the
   *  fulfillment qty in product UoM). When packaging is set, qty is
   *  derived as packaging.qty * packaging_qty unless the user manually
   *  overrides qty. */
  packaging_qty: string
  /** Tracks whether autofill ran for the current product — guards
   *  against autofill stomping a user's manual edit when they're
   *  iterating between fields. */
  autofilled_for_product_id: number | null
}

let _localKey = -1
function nextLocalKey(): number {
  _localKey -= 1
  return _localKey
}

function makeBlankLine(): DraftLine {
  return {
    key: nextLocalKey(),
    product: null,
    customer_part_number: '',
    description: '',
    quantity: '',
    unit: '',
    unit_price: '',
    vendor_unit_cost: '',
    lead_time_days: '',
    selected_vendor_id: null,
    packaging_id: null,
    packaging_qty: '',
    autofilled_for_product_id: null,
  }
}

function lineFromServer(line: QuoteLineRead): DraftLine {
  return {
    key: line.id,
    product:
      line.product_id !== null
        ? [line.product_id, line.description ?? `product #${line.product_id}`]
        : null,
    customer_part_number: line.customer_part_number ?? '',
    description: line.description ?? '',
    quantity: line.quantity ?? '',
    unit: line.unit ?? '',
    unit_price: line.unit_price ?? '',
    vendor_unit_cost: line.vendor_unit_cost ?? '',
    lead_time_days:
      line.lead_time_days !== null ? String(line.lead_time_days) : '',
    selected_vendor_id: line.selected_vendor_id,
    packaging_id: line.packaging_id,
    packaging_qty: line.packaging_qty ?? '',
    autofilled_for_product_id: line.product_id,
  }
}

function draftLineToWrite(line: DraftLine, sortOrder: number): QuoteLineWrite | null {
  if (!line.product || !line.quantity || !line.unit_price) return null
  const qty = Number(line.quantity)
  const price = Number(line.unit_price)
  if (Number.isNaN(qty) || Number.isNaN(price)) return null
  return {
    sort_order: sortOrder,
    product_id: line.product[0],
    customer_part_number: line.customer_part_number || null,
    description: line.description || null,
    quantity: line.quantity,
    unit: line.unit || null,
    unit_price: line.unit_price,
    selected_vendor_id: line.selected_vendor_id,
    vendor_unit_cost: line.vendor_unit_cost || null,
    lead_time_days:
      line.lead_time_days ? Number(line.lead_time_days) : null,
    packaging_id: line.packaging_id,
    packaging_qty: line.packaging_qty || null,
  }
}

/** Computed margin % for a line: (price - cost) / price. Returns
 *  null when either side is missing or zero. */
function lineMarginPct(line: DraftLine): number | null {
  const price = Number(line.unit_price)
  const cost = Number(line.vendor_unit_cost)
  if (!Number.isFinite(price) || price <= 0) return null
  if (!Number.isFinite(cost) || cost <= 0) return null
  return ((price - cost) / price) * 100
}

function lineExtended(line: DraftLine): number {
  const q = Number(line.quantity)
  const p = Number(line.unit_price)
  if (!Number.isFinite(q) || !Number.isFinite(p)) return 0
  return q * p
}

function lineExtendedCost(line: DraftLine): number {
  const q = Number(line.quantity)
  const c = Number(line.vendor_unit_cost)
  if (!Number.isFinite(q) || !Number.isFinite(c)) return 0
  return q * c
}

export interface QuoteDetailProps {
  quoteId: number | null
  summary?: QuoteSummary
  onCreated?: (newId: number) => void
  onCancelCreate?: () => void
}

export function QuoteDetail({
  quoteId,
  onCreated,
  onCancelCreate,
}: QuoteDetailProps) {
  const isCreateMode = quoteId === null
  const navigate = useNavigate()

  const { data: quote, isLoading, isError, error } = useQuote(quoteId)

  const create = useCreateQuote()
  const update = useUpdateQuote()
  const submit = useSubmitQuote()
  const approve = useApproveQuote()
  const reject = useRejectQuote()
  const customerReject = useCustomerReject()
  const transition = useTransitionQuote()
  const recordPo = usePoReceived()
  const convertToSo = useConvertToSo()
  const renderPdf = useRenderQuotePdf()

  // ─── Draft state (create + edit) ───────────────────────────────────
  const [customer, setCustomer] = useState<PickerValue>(null)
  const [validThrough, setValidThrough] = useState('')
  const [paymentTerms, setPaymentTerms] = useState('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<DraftLine[]>([makeBlankLine()])
  const [sendDialogOpen, setSendDialogOpen] = useState(false)

  const columnCfg = useColumnConfig({
    storageKey: 'quote-line-editor',
    columns: LINE_COLUMNS,
  })

  useEffect(() => {
    if (isCreateMode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets the form on create-mode entry; isCreateMode is the gate so no loop.
      setCustomer(null)
      setValidThrough('')
      setPaymentTerms('')
      setNotes('')
      setLines([makeBlankLine()])
      return
    }
    if (quote) {
      // Preserve the picker label across refetches: if the user just
      // picked a customer, the in-state label is the real partner
      // name. After save the detail refetches and we'd otherwise
      // clobber it with the `customer #N` placeholder, making the
      // edit look reverted. Keep the existing state when ids match.
      setCustomer((prev) => {
        if (quote.customer_id === null) return null
        const fallbackLabel =
          quote.customer_name && quote.customer_name.trim().length > 0
            ? quote.customer_name
            : `customer #${quote.customer_id}`
        if (prev && prev[0] === quote.customer_id) {
          // Upgrade the label if server now has a real name and we're
          // still showing the placeholder.
          if (
            quote.customer_name &&
            prev[1].startsWith('customer #')
          ) {
            return [quote.customer_id, quote.customer_name]
          }
          return prev
        }
        return [quote.customer_id, fallbackLabel]
      })
      setValidThrough(
        quote.valid_through
          ? quote.valid_through.slice(0, 10)
          : '',
      )
      setPaymentTerms(quote.payment_terms ?? '')
      setNotes(quote.notes ?? '')
      setLines(
        quote.lines.length > 0
          ? quote.lines.map(lineFromServer)
          : [makeBlankLine()],
      )
    }
  }, [quote, isCreateMode])

  const isEditableStatus = quote ? EDITABLE_STATUSES.has(quote.status) : false
  const isFormMode = isCreateMode || isEditableStatus

  if (!isCreateMode && isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-secondary">
        Loading quote…
      </div>
    )
  }
  if (!isCreateMode && (isError || !quote)) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load quote"
        description={
          error instanceof ApiError
            ? error.message
            : 'The /api/quotes/{id} endpoint returned an error.'
        }
      />
    )
  }

  function handleSave() {
    // Sprint 12b: drafts can be saved partially. Customer + complete
    // lines aren't required for save — they're enforced at /submit.
    const writeLines: QuoteLineWrite[] = []
    for (let i = 0; i < lines.length; i += 1) {
      const w = draftLineToWrite(lines[i], i)
      if (w) writeLines.push(w)
    }
    const body: QuoteWrite = {
      customer_id: customer ? customer[0] : null,
      valid_through: validThrough ? new Date(validThrough).toISOString() : null,
      payment_terms: paymentTerms.trim() || null,
      notes: notes.trim() || null,
      lines: writeLines,
    }

    if (isCreateMode) {
      create.mutate(body, {
        onSuccess: (created) => {
          toast.success(`Created ${created.quote_number}`)
          onCreated?.(created.id)
        },
        onError: (err) => {
          toast.danger(
            err instanceof ApiError ? err.message : 'Create failed',
          )
        },
      })
      return
    }
    if (quoteId === null) return
    update.mutate(
      { id: quoteId, body },
      {
        onSuccess: (saved) => toast.success(`Saved ${saved.quote_number}`),
        onError: (err) => {
          toast.danger(
            err instanceof ApiError ? err.message : 'Save failed',
          )
        },
      },
    )
  }

  function updateLine(key: number, patch: Partial<DraftLine>) {
    setLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    )
  }

  function removeLine(key: number) {
    setLines((prev) =>
      prev.length === 1 ? [makeBlankLine()] : prev.filter((l) => l.key !== key),
    )
  }

  function addLine() {
    setLines((prev) => [...prev, makeBlankLine()])
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4 overflow-y-auto">
      {/* ─── Header ─── */}
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-role-page-title">
            {isCreateMode ? 'New quote' : quote?.quote_number}
          </h2>
          {quote ? <QuoteStatusChip status={quote.status} /> : null}
          {quote?.deleted_at ? (
            <Chip size="xs" variant="neutral">Archived</Chip>
          ) : null}
          {quote?.odoo_so_id ? (
            <Chip size="xs" variant="success">
              SO #{quote.odoo_so_id}
            </Chip>
          ) : null}
        </div>
      </header>

      {/* ─── Form (create + editable-status edit) ─── */}
      {isFormMode ? (
        <>
          {/* Header form (no Notes — Notes lives below the lines section) */}
          <section className="grid grid-cols-2 gap-3 rounded-md border border-border-subtle bg-surface p-3">
            <label className="flex flex-col gap-1 text-sm col-span-2">
              <span className="text-secondary">Customer</span>
              <PartnerPicker
                kind="customer"
                value={customer}
                onChange={setCustomer}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-secondary">Valid through</span>
              <input
                type="date"
                value={validThrough}
                onChange={(e) => setValidThrough(e.target.value)}
                className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm focus:border-border-focus focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-secondary">Payment terms</span>
              <input
                type="text"
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                placeholder="Net 30"
                className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm focus:border-border-focus focus:outline-none"
              />
            </label>
          </section>

          {/* Line editor */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-role-section-label">Lines</h3>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={addLine}>
                  <Plus size={12} /> Add line
                </Button>
                <ColumnConfigMenu config={columnCfg} />
              </div>
            </div>
            <div className="overflow-x-auto rounded-md border border-border-subtle">
              <table className="text-sm" style={{ minWidth: '1500px' }}>
                <thead className="bg-surface-sunk">
                  <tr>
                    {columnCfg.visibleColumns.map((col) => (
                      <SortableHeader
                        key={col.key}
                        column={col}
                        sortKey={columnCfg.sortKey}
                        sortDir={columnCfg.sortDir}
                        onSort={columnCfg.toggleSort}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {columnCfg.sortRows(lines, lineSortValue).map((line) => (
                    <LineRow
                      key={line.key}
                      line={line}
                      visibleColumns={columnCfg.visibleColumns}
                      customerId={customer ? customer[0] : null}
                      onPatch={(patch) => updateLine(line.key, patch)}
                      onRemove={() => removeLine(line.key)}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <FooterRows
                    lines={lines}
                    visibleColumns={columnCfg.visibleColumns}
                  />
                </tfoot>
              </table>
            </div>
          </section>

          {/* Notes (D-3 — below the line editor, not above) */}
          <section className="rounded-md border border-border-subtle bg-surface p-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-secondary">Notes</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Internal notes — appended to the quote PDF"
                className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm focus:border-border-focus focus:outline-none"
              />
            </label>
          </section>

          {/* Terms & Conditions link */}
          <p className="text-sm text-secondary">
            Terms &amp; Conditions:{' '}
            <a
              href="https://www.siliconesplus.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              https://www.siliconesplus.com/terms
            </a>
          </p>

          {/* Save / Cancel */}
          <section className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              onClick={handleSave}
              loading={create.isPending || update.isPending}
            >
              <Save size={14} />
              {isCreateMode ? 'Create draft' : 'Save changes'}
            </Button>
            {isCreateMode && onCancelCreate ? (
              <Button variant="ghost" onClick={onCancelCreate}>
                <X size={14} /> Cancel
              </Button>
            ) : null}
          </section>
        </>
      ) : null}

      {/* ─── Action bar (post-draft) ─── */}
      {!isCreateMode && quote && !isEditableStatus ? (
        <ActionButtons
          quote={quote}
          onApprove={() => approve.mutate(quote.id)}
          onReject={() => {
            const reason = window.prompt('Reason for revision?')
            if (reason) reject.mutate({ id: quote.id, body: { reason } })
          }}
          onCustomerReject={() => {
            const reason = window.prompt('Why did the customer reject?')
            if (reason) customerReject.mutate({ id: quote.id, body: { reason } })
          }}
          onSend={() => setSendDialogOpen(true)}
          onRecordPo={() => {
            const num = window.prompt('Customer PO number?')
            if (num) recordPo.mutate({ id: quote.id, body: { customer_po_number: num } })
          }}
          onConvert={() => convertToSo.mutate(quote.id)}
          onRevise={() => {
            // Sprint 12d — Revise transitions to draft AND navigates
            // to the full-page detail so the line editor gets the
            // full viewport (was Drawer; cramped at 640px).
            transition.mutate(
              { id: quote.id, body: { to: 'draft' } },
              {
                onSuccess: () => {
                  navigate({
                    to: '/quotes/$quoteId',
                    params: { quoteId: String(quote.id) },
                  })
                },
              },
            )
          }}
          onRenderPdf={() =>
            renderPdf.mutate(quote.id, {
              onSuccess: (data) => window.open(data.download_url, '_blank'),
            })
          }
          pending={
            submit.isPending ||
            approve.isPending ||
            reject.isPending ||
            customerReject.isPending ||
            transition.isPending ||
            recordPo.isPending ||
            convertToSo.isPending ||
            renderPdf.isPending
          }
        />
      ) : null}

      {/* ─── Submit/draft action bar (visible while editing draft) ─── */}
      {!isCreateMode && quote && quote.status === 'draft' ? (
        <section className="flex flex-wrap items-center gap-2 rounded-md border border-border-subtle bg-surface-sunk p-3">
          <Button size="sm" variant="primary" onClick={() => submit.mutate(quote.id)} disabled={submit.isPending || quote.lines.length === 0}>
            <Send size={14} />
            Submit for review
          </Button>
          <Button size="sm" variant="ghost" onClick={() =>
            renderPdf.mutate(quote.id, {
              onSuccess: (data) => window.open(data.download_url, '_blank'),
            })
          }>
            Preview PDF
          </Button>
          <span className="text-xs text-tertiary">
            Save changes before submitting. Submission requires at least one line.
          </span>
        </section>
      ) : null}

      {/* ─── Read-only summary tables for non-editable + history ─── */}
      {!isCreateMode && quote ? (
        <>
          {!isEditableStatus ? <QuoteHeaderRO quote={quote} /> : null}
          {!isEditableStatus ? <QuoteLineTable quote={quote} /> : null}
          <QuoteHistory quote={quote} />
        </>
      ) : null}

      <SendQuoteDialog
        open={sendDialogOpen}
        quoteId={quote?.id ?? null}
        quoteNumber={quote?.quote_number ?? null}
        onClose={() => setSendDialogOpen(false)}
      />
    </div>
  )
}

function QuoteHeaderRO({ quote }: { quote: Quote }) {
  return (
    <section className="grid grid-cols-2 gap-x-6 gap-y-1 rounded-md border border-border-subtle bg-surface p-3 text-sm">
      <KV k="Customer">{quote.customer_name || `customer #${quote.customer_id}`}</KV>
      <KV k="Valid through">
        {quote.valid_through
          ? new Date(quote.valid_through).toLocaleDateString()
          : '—'}
      </KV>
      <KV k="Payment terms">{quote.payment_terms || '—'}</KV>
      <KV k="Created">
        {quote.created_at
          ? new Date(quote.created_at).toLocaleDateString()
          : '—'}
      </KV>
      {quote.customer_po_number ? (
        <KV k="Customer PO">{quote.customer_po_number}</KV>
      ) : null}
      {quote.sent_at ? (
        <KV k="Sent at">{new Date(quote.sent_at).toLocaleString()}</KV>
      ) : null}
      {quote.notes ? (
        <div className="col-span-2 mt-1 text-sm text-secondary">
          {quote.notes}
        </div>
      ) : null}
    </section>
  )
}

function KV({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-tertiary uppercase tracking-wide">{k}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  )
}

interface ActionButtonsProps {
  quote: Quote
  pending: boolean
  onApprove: () => void
  onReject: () => void
  onCustomerReject: () => void
  onSend: () => void
  onRecordPo: () => void
  onConvert: () => void
  onRevise: () => void
  onRenderPdf: () => void
}

function ActionButtons(props: ActionButtonsProps) {
  const { quote, pending } = props
  const hasPo = Boolean(quote.customer_po_number)

  if (quote.status === 'customer_approved') {
    return (
      <section className="flex items-center gap-2 rounded-md border border-border-subtle bg-surface-sunk p-3">
        <Chip size="xs" variant="success">Customer Approved</Chip>
        <span className="text-sm">
          Linked to Odoo sale.order #{quote.odoo_so_id}.
        </span>
      </section>
    )
  }

  return (
    <section className="flex flex-wrap items-center gap-2 rounded-md border border-border-subtle bg-surface-sunk p-3">
      <Button size="sm" variant="ghost" onClick={props.onRenderPdf}>
        Preview PDF
      </Button>
      {quote.status === 'in_review' ? (
        <>
          <Button size="sm" variant="primary" onClick={props.onApprove} disabled={pending}>
            <CheckCircle2 size={14} /> Approve pricing
          </Button>
          <Button size="sm" variant="ghost" onClick={props.onReject} disabled={pending}>
            Request revisions
          </Button>
        </>
      ) : null}
      {(quote.status === 'revise_pricing' || quote.status === 'customer_rejected') ? (
        <>
          {quote.rejection_reason ? (
            <span className="text-sm text-danger">
              {quote.status === 'customer_rejected' ? 'Customer rejection' : 'Revision request'}:{' '}
              {quote.rejection_reason}
            </span>
          ) : null}
          <Button
            size="sm"
            variant="primary"
            onClick={props.onRevise}
            disabled={pending}
            title="Move back to draft so you can revise and resubmit"
          >
            <RotateCcw size={14} />
            Revise
          </Button>
        </>
      ) : null}
      {quote.status === 'approved' ? (
        <Button size="sm" variant="primary" onClick={props.onSend} disabled={pending}>
          <Send size={14} />
          Send to customer
        </Button>
      ) : null}
      {(quote.status === 'approved' || quote.status === 'sent') ? (
        <>
          <Button size="sm" variant={hasPo ? 'ghost' : 'primary'} onClick={props.onRecordPo} disabled={pending}>
            {hasPo ? 'Update PO' : 'Record customer PO'}
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={props.onConvert}
            disabled={pending || !hasPo}
            title={hasPo ? 'Customer accepted — create Odoo SO from this quote + PO' : 'Record a customer PO first'}
          >
            <ExternalLink size={14} />
            Customer approved (create SO)
          </Button>
        </>
      ) : null}
      {quote.status === 'sent' ? (
        <Button size="sm" variant="ghost" onClick={props.onCustomerReject} disabled={pending}>
          Customer rejected…
        </Button>
      ) : null}
    </section>
  )
}

// CheckCircle2 imported at top.

function QuoteLineTable({ quote }: { quote: Quote }) {
  if (quote.lines.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border-subtle p-4 text-sm text-secondary">
        No lines.
      </div>
    )
  }
  const total = quote.lines.reduce((sum, line) => {
    const q = Number(line.quantity ?? 0)
    const p = Number(line.unit_price ?? 0)
    return sum + q * p
  }, 0)
  return (
    <section>
      <h3 className="text-role-section-label mb-2">Lines</h3>
      <div className="overflow-x-auto rounded-md border border-border-subtle">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-sunk">
            <tr>
              <th className="text-role-table-header px-3 py-2 text-right">Qty</th>
              <th className="text-role-table-header px-3 py-2 text-left">Customer Part #</th>
              <th className="text-role-table-header px-3 py-2 text-left">Description</th>
              <th className="text-role-table-header px-3 py-2 text-right">Unit price</th>
              <th className="text-role-table-header px-3 py-2 text-right">Extended</th>
            </tr>
          </thead>
          <tbody>
            {quote.lines.map((line) => {
              const q = Number(line.quantity ?? 0)
              const p = Number(line.unit_price ?? 0)
              const ext = q * p
              return (
                <tr key={line.id} className="border-t border-border-subtle">
                  <td className="text-role-table-cell px-3 py-2 text-right">
                    {q} {line.unit ?? ''}
                  </td>
                  <td className="text-role-table-cell px-3 py-2">
                    {line.customer_part_number ?? '—'}
                  </td>
                  <td className="text-role-table-cell px-3 py-2">
                    {line.description ?? `Product #${line.product_id}`}
                  </td>
                  <td className="text-role-table-cell px-3 py-2 text-right">
                    ${p.toFixed(2)}
                  </td>
                  <td className="text-role-table-cell px-3 py-2 text-right">
                    ${ext.toFixed(2)}
                  </td>
                </tr>
              )
            })}
            <tr className="border-t-2 border-border bg-surface-sunk font-semibold">
              <td colSpan={4} className="px-3 py-2 text-right">
                Total
              </td>
              <td className="px-3 py-2 text-right">${total.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  )
}

interface LineRowProps {
  line: DraftLine
  visibleColumns: ReadonlyArray<ColumnDef>
  customerId: number | null
  onPatch: (patch: Partial<DraftLine>) => void
  onRemove: () => void
}

function LineRow({
  line, visibleColumns, customerId, onPatch, onRemove,
}: LineRowProps) {
  const productId = line.product ? line.product[0] : null
  const { data: ctx } = useLineContext({ productId, customerId })

  // Auto-fill once per product change. Marker prevents re-stomping
  // when the user edits a field after autofill — context refetches
  // don't replay onto user changes.
  useEffect(() => {
    if (!ctx || !productId) return
    if (line.autofilled_for_product_id === productId) return
    const patch: Partial<DraftLine> = {
      autofilled_for_product_id: productId,
    }
    if (ctx.uom?.name && !line.unit) patch.unit = ctx.uom.name
    if (ctx.customer_part_number && !line.customer_part_number) {
      patch.customer_part_number = ctx.customer_part_number
    }
    if (!line.unit_price) {
      const price = ctx.customer_price ?? ctx.product?.list_price ?? null
      if (price !== null) patch.unit_price = String(price)
    }
    if (!line.vendor_unit_cost && ctx.best_vendor_cost !== null) {
      patch.vendor_unit_cost = String(ctx.best_vendor_cost)
    }
    if (!line.lead_time_days && ctx.best_vendor_lead_days !== null) {
      patch.lead_time_days = String(ctx.best_vendor_lead_days)
    }
    if (line.selected_vendor_id === null && ctx.best_vendor_id !== null) {
      patch.selected_vendor_id = ctx.best_vendor_id
    }
    if (!line.description && ctx.product?.name) {
      patch.description = ctx.product.name
    }
    onPatch(patch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, productId])

  const margin = lineMarginPct(line)
  const extended = lineExtended(line)

  // Packagings: prefer sales-flagged when at least one is set;
  // otherwise show all (some products tag everything as purchase-only).
  const packagings = ctx?.packagings ?? []
  const salesPackagings = packagings.filter((p) => p.sales)
  const visiblePackagings = salesPackagings.length > 0 ? salesPackagings : packagings
  const selectedPackagingId = line.packaging_id

  function selectPackaging(packagingIdRaw: string) {
    if (!packagingIdRaw) {
      onPatch({ packaging_id: null, packaging_qty: '' })
      return
    }
    const id = Number(packagingIdRaw)
    const pkg = visiblePackagings.find((p) => p.id === id)
    if (!pkg) return
    // Default Pkg Qty to 1 and propagate qty = pkg.qty * 1 so the
    // user sees a sensible starting point. They can override either
    // field afterwards.
    const pkgQty = '1'
    const totalQty =
      pkg.qty !== null ? String(pkg.qty) : line.quantity
    onPatch({
      packaging_id: id,
      packaging_qty: pkgQty,
      quantity: totalQty,
    })
  }

  function setPackagingQty(raw: string) {
    const patch: Partial<DraftLine> = { packaging_qty: raw }
    const pkg = visiblePackagings.find((p) => p.id === selectedPackagingId)
    if (pkg && pkg.qty !== null && raw) {
      const n = Number(raw)
      if (Number.isFinite(n)) {
        patch.quantity = String(pkg.qty * n)
      }
    }
    onPatch(patch)
  }

  function renderCell(col: ColumnDef): React.ReactNode {
    switch (col.key) {
      case 'product':
        return (
          <ProductPicker
            value={line.product}
            onChange={(v) => onPatch({ product: v })}
            excludeSamples
          />
        )
      case 'customer_part_number':
        return (
          <input
            type="text"
            value={line.customer_part_number}
            onChange={(e) => onPatch({ customer_part_number: e.target.value })}
            className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm focus:border-border-focus focus:outline-none"
          />
        )
      case 'quantity':
        return (
          <input
            type="number"
            step="any"
            value={line.quantity}
            onChange={(e) => onPatch({ quantity: e.target.value })}
            className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-right focus:border-border-focus focus:outline-none"
          />
        )
      case 'unit':
        return (
          <input
            type="text"
            value={line.unit}
            onChange={(e) => onPatch({ unit: e.target.value })}
            placeholder={ctx?.uom?.name ?? ''}
            className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm focus:border-border-focus focus:outline-none"
          />
        )
      case 'packaging':
        if (visiblePackagings.length === 0) {
          return <span className="text-xs text-tertiary">{ctx ? '—' : ''}</span>
        }
        return (
          <select
            value={selectedPackagingId !== null ? String(selectedPackagingId) : ''}
            onChange={(e) => selectPackaging(e.target.value)}
            className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm focus:border-border-focus focus:outline-none"
          >
            <option value="">— pick package —</option>
            {visiblePackagings.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
                {p.qty !== null ? ` (${p.qty} ${ctx?.uom?.name ?? ''})` : ''}
              </option>
            ))}
          </select>
        )
      case 'packaging_qty':
        return (
          <input
            type="number"
            step="any"
            min="0"
            value={line.packaging_qty}
            onChange={(e) => setPackagingQty(e.target.value)}
            disabled={selectedPackagingId === null}
            placeholder={selectedPackagingId === null ? '—' : '1'}
            className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-right focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-tertiary"
          />
        )
      case 'unit_price':
        return (
          <input
            type="number"
            step="any"
            value={line.unit_price}
            onChange={(e) => onPatch({ unit_price: e.target.value })}
            className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-right focus:border-border-focus focus:outline-none"
          />
        )
      case 'last_paid':
        if (ctx?.last_paid !== null && ctx?.last_paid !== undefined) {
          return (
            <span className="text-xs" title={ctx.last_paid_at ?? undefined}>
              ${ctx.last_paid.toFixed(2)}
            </span>
          )
        }
        return <span className="text-tertiary text-xs">—</span>
      case 'vendor_unit_cost':
        return (
          <input
            type="number"
            step="any"
            value={line.vendor_unit_cost}
            onChange={(e) => onPatch({ vendor_unit_cost: e.target.value })}
            placeholder={
              ctx?.best_vendor_cost !== null && ctx?.best_vendor_cost !== undefined
                ? ctx.best_vendor_cost.toFixed(2)
                : ''
            }
            className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-right focus:border-border-focus focus:outline-none"
          />
        )
      case 'margin':
        return (
          <span className={[
            'text-sm',
            margin === null ? 'text-tertiary' :
              margin < 15 ? 'text-danger' :
              margin < 25 ? 'text-warning' : '',
          ].join(' ')}>
            {margin !== null ? `${margin.toFixed(1)}%` : '—'}
          </span>
        )
      case 'extended':
        return <span className="text-sm">${extended.toFixed(2)}</span>
      case 'actions':
        return (
          <Button
            size="sm"
            variant="ghost"
            iconOnly
            aria-label="Remove line"
            onClick={onRemove}
          >
            <Trash2 size={14} />
          </Button>
        )
      default:
        return null
    }
  }

  return (
    <tr className="border-t border-border-subtle align-top">
      {visibleColumns.map((col) => {
        const align = col.align ?? 'left'
        const alignClass =
          align === 'right' ? 'text-right' :
          align === 'center' ? 'text-center' : 'text-left'
        return (
          <td
            key={col.key}
            className={`px-2 py-1 ${alignClass}`}
            style={col.width ? { width: col.width } : undefined}
          >
            {renderCell(col)}
          </td>
        )
      })}
    </tr>
  )
}

interface FooterRowsProps {
  lines: DraftLine[]
  visibleColumns: ReadonlyArray<ColumnDef>
}

function FooterRows({ lines, visibleColumns }: FooterRowsProps) {
  const totalRevenue = lines.reduce((s, l) => s + lineExtended(l), 0)
  const totalCost = lines.reduce((s, l) => s + lineExtendedCost(l), 0)
  const colCount = visibleColumns.length
  const extendedColIdx = visibleColumns.findIndex((c) => c.key === 'extended')
  const marginColIdx = visibleColumns.findIndex((c) => c.key === 'margin')

  // Position the total at the Extended column when visible, else
  // span the whole row.
  const totalLabelSpan = extendedColIdx >= 0 ? extendedColIdx : Math.max(0, colCount - 1)
  const totalValueSpan = extendedColIdx >= 0 ? colCount - extendedColIdx - 1 : 0

  return (
    <>
      <tr className="border-t-2 border-border bg-surface-sunk font-semibold">
        <td colSpan={totalLabelSpan} className="px-3 py-2 text-right">
          Quote total
        </td>
        {extendedColIdx >= 0 ? (
          <>
            <td className="px-3 py-2 text-right">
              ${totalRevenue.toFixed(2)}
            </td>
            {totalValueSpan > 0 ? <td colSpan={totalValueSpan} /> : null}
          </>
        ) : null}
      </tr>
      {totalRevenue > 0 && totalCost > 0 ? (() => {
        const marginPct = ((totalRevenue - totalCost) / totalRevenue) * 100
        const marginUsd = totalRevenue - totalCost
        const marginLabelSpan = marginColIdx >= 0 ? marginColIdx : Math.max(0, colCount - 1)
        const tailSpan = marginColIdx >= 0 ? colCount - marginColIdx - 2 : 0
        return (
          <tr className="bg-surface-sunk text-secondary">
            <td colSpan={marginLabelSpan} className="px-3 py-2 text-right">
              Quote margin
            </td>
            {marginColIdx >= 0 ? (
              <>
                <td className="px-3 py-2 text-right">{marginPct.toFixed(1)}%</td>
                {extendedColIdx > marginColIdx ? (
                  <td className="px-3 py-2 text-right">${marginUsd.toFixed(2)}</td>
                ) : null}
                {tailSpan > 0 ? <td colSpan={tailSpan} /> : null}
              </>
            ) : null}
          </tr>
        )
      })() : null}
    </>
  )
}

function QuoteHistory({ quote }: { quote: Quote }) {
  if (quote.history.length === 0) return null
  return (
    <section>
      <h3 className="text-role-section-label mb-2">History</h3>
      <ul className="space-y-1 text-sm">
        {quote.history.slice(0, 10).map((h) => (
          <li key={h.id} className="text-secondary">
            <span className="font-medium text-primary">
              {h.from_status ?? '—'} → {h.to_status}
            </span>
            {h.transitioned_by ? <> by {h.transitioned_by}</> : null}
            {h.transitioned_at ? (
              <span className="ml-2 text-tertiary">
                {new Date(h.transitioned_at).toLocaleString()}
              </span>
            ) : null}
            {h.comment ? <span className="block ml-4">{h.comment}</span> : null}
          </li>
        ))}
      </ul>
    </section>
  )
}
